import "@/App.css";
import { lazy, Suspense, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Rocket } from "lucide-react";

import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import FeedbackWidget from "@/components/feedback/FeedbackWidget";

// Route-based code splitting with preload support (instant transitions once preloaded).
function lazyWithPreload(factory) { const C = lazy(factory); C.preload = factory; return C; }
const Home = lazyWithPreload(() => import("@/pages/Home"));
const Onboarding = lazyWithPreload(() => import("@/pages/Onboarding"));
const Workspace = lazyWithPreload(() => import("@/pages/Workspace"));
const Portfolio = lazyWithPreload(() => import("@/pages/Portfolio"));
const Analytics = lazyWithPreload(() => import("@/pages/Analytics"));
const Pricing = lazyWithPreload(() => import("@/pages/Pricing"));
const Interview = lazyWithPreload(() => import("@/pages/Interview"));
const Resume = lazyWithPreload(() => import("@/pages/Resume"));
const Admin = lazyWithPreload(() => import("@/pages/Admin"));
const Employer = lazyWithPreload(() => import("@/pages/Employer"));
const Changelog = lazyWithPreload(() => import("@/pages/Changelog"));
const BetaJoin = lazyWithPreload(() => import("@/pages/BetaJoin"));
const Info = lazyWithPreload(() => import("@/pages/Info"));

function Loader() {
  const [slow, setSlow] = useState(false);
  useEffect(() => { const t = setTimeout(() => setSlow(true), 400); return () => clearTimeout(t); }, []);
  return (
    <div className="min-h-screen grid place-items-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <Rocket className="w-7 h-7 text-volt animate-pulse" />
        {slow && <span className="text-xs text-muted-foreground font-mono">Loading…</span>}
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function RoleGate({ need, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/" replace />;
  if (!user.entitlements?.[need]) return <Navigate to="/hq" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Preload the most likely next screens during browser idle time (after first paint).
  useEffect(() => {
    const preload = () => [Home, Workspace, Onboarding, Resume, Portfolio, Analytics].forEach((c) => c.preload && c.preload());
    const ric = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
    const id = ric(preload);
    return () => (window.cancelIdleCallback ? window.cancelIdleCallback(id) : clearTimeout(id));
  }, []);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/privacy" element={<Info />} />
        <Route path="/terms" element={<Info />} />
        <Route path="/accessibility" element={<Info />} />
        <Route path="/hq" element={<Protected><Home /></Protected>} />
        <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
        <Route path="/workspace/:id" element={<Protected><Workspace /></Protected>} />
        <Route path="/portfolio" element={<Protected><Portfolio /></Protected>} />
        <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
        <Route path="/interview" element={<Protected><Interview /></Protected>} />
        <Route path="/resume" element={<Protected><Resume /></Protected>} />
        <Route path="/changelog" element={<Protected><Changelog /></Protected>} />
        <Route path="/beta" element={<Protected><BetaJoin /></Protected>} />
        <Route path="/admin" element={<RoleGate need="admin"><Admin /></RoleGate>} />
        <Route path="/employer" element={<RoleGate need="employer_dashboard"><Employer /></RoleGate>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App grain">
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <FeedbackWidget />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
